import { useMemo } from 'react'

import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'

import { VertSpacerMd } from '../layout'

export enum ConfirmModalAction {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Pending = 'pending',
  Neutral = 'neutral'
}

export interface IConfirmModalProps {
  text: string
  confirmText?: string
  cancelText?: string
  description: string
  action: 'success' | 'warning' | 'error' | 'pending' | 'neutral'
  cancelAction: 'success' | 'warning' | 'error' | 'pending' | 'neutral'
  ignoreCtxTheme?: boolean
}

export const ConfirmModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IConfirmModalProps
}) => {
  const { text, confirmText, cancelText, description, action, cancelAction, ignoreCtxTheme } = componentProps

  const colors = useMemo(() => {
    switch (action) {
      case ConfirmModalAction.Success:
        return 'action-success'

      case ConfirmModalAction.Warning:
        return 'action-warning'

      case ConfirmModalAction.Error:
        return 'action-error'

      case ConfirmModalAction.Pending:
        return 'action-info'

      default:
        return 'action-neutral'
    }
  }, [action])

  const cancelColors = useMemo(() => {
    switch (cancelAction) {
      case ConfirmModalAction.Success:
        return 'action-success'

      case ConfirmModalAction.Warning:
        return 'action-warning'

      case ConfirmModalAction.Error:
        return 'action-error'

      case ConfirmModalAction.Pending:
        return 'action-info'

      default:
        return 'action-neutral'
    }
  }, [cancelAction])

  return (
    <ModalContainer classes='!items-center px-[1rem]' innerClasses='rounded-3xl !min-h-0 !h-auto !min-w-0 md:!w-[400px] !w-auto'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Confirmation' title={text} />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} ignoreCtxTheme={ignoreCtxTheme} />
        </div>
      </ModalHeaderContainer>

      <ModalBody classes='!pt-[100px]'>
        <p className='text-text text-[0.9em] px-[5px] whitespace-pre-line'>{description}</p>
        <VertSpacerMd />
      </ModalBody>
      <ModalFooter>
        <div className='w-full flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            type='button'
            text={confirmText || 'Confirm'}
            loading={false}
            className={colors}
            callback={() => onDismiss(null, ModalDismiss.Confirm)}
          />
          <ButtonRoundedSm
            type='button'
            text={cancelText || 'Cancel'}
            loading={false}
            className={cancelColors}
            callback={() => onDismiss(null, ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
